import type {
  FunctionComponent,
  KeyboardEvent,
  RefObject,
  SyntheticEvent,
} from 'react';
import React, { useCallback, useRef } from 'react';
import cx from 'classnames';

import { isSubmitEvent } from '@trello/keybindings';
import { Tooltip } from '@trello/nachos/tooltip';
import { LabelsPopoverTestIds } from '@trello/test-ids';
import { useGlobalTheme } from '@trello/theme';

import type { CardLabelType } from './CardLabel.types';
import { formatLabelTooltip } from './formatLabelTooltip';
import { flattenLabelColor } from './labelColors';
import { mergeRefs } from './mergeRefs';
import { useDraggableLabel } from './useDraggableLabel';

import styles from './CardLabel.less';

export interface CardLabelProps {
  label: CardLabelType;
  className?: string;
  /**
   * If onClick is defined, the label is rendered as a <button> element.
   */
  onClick?: (e: SyntheticEvent) => void;
  /**
   * Optional override for onKeyDown event handler. By default, submit keypress
   * events trigger the associated onClick prop.
   */
  onKeyDown?: (e: KeyboardEvent<HTMLButtonElement | HTMLDivElement>) => void;
  /**
   * Whether or not the label can be dragged and dropped into a different place.
   * The drop target has to handle the native drop event itself; see
   * app/scripts/views/card/CardViewAttachment.js::dropLabel for an example.
   * @default false
   */
  isDraggable?: boolean;
  /**
   * Whether or not to enable hover states for the label output. Adding an
   * `onClick` handler will have the same output, but this is a fallback in case
   * hover colors are desired without any click interactions.
   * @default false
   */
  isHoverable?: boolean;
  /**
   * Whether or not the label should stretch to take up the whole width of its
   * parent container.
   * @default false
   */
  fullWidth?: boolean;
  /**
   * Optional tabIndex prop. Set this to -1 to disable keyboard interaction.
   * @default 0
   */
  tabIndex?: number;
}

/**
 * Pass-through component that renders the old Label component by default,
 * and a redesign otherwise.
 */
export const CardLabel: FunctionComponent<CardLabelProps> = ({
  label,
  children,
  className: propsClassName,
  isDraggable,
  isHoverable,
  fullWidth,
  tabIndex = 0,
  onClick,
  onKeyDown,
}) => {
  const ref = useRef<HTMLButtonElement | HTMLDivElement | null>(null);

  const submitOnKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      if (isSubmitEvent(e)) {
        onClick?.(e);
      }
    },
    [onClick],
  );

  const { onDragStart, onDragEnd } = useDraggableLabel(label, ref);

  const { effectiveColorMode } = useGlobalTheme();

  // We're moving away from the dot design when a theme is active, as ADS colors
  // have more accessible contrast ratios for labels.
  const shouldShowDot = effectiveColorMode === null && !!label.color;

  const className = cx(
    styles.label,
    label.color ? styles[label.color] : styles.colorless,
    fullWidth && styles['label--fullwidth'],
    (onClick || isHoverable) && styles['label--hoverable'],
    shouldShowDot && styles['label--with-dot'],
    label.color && `color-blind-pattern-${flattenLabelColor(label.color)}`,
    propsClassName,
  );
  const tooltip = formatLabelTooltip(label);

  return (
    <Tooltip content={tooltip} hideTooltipOnMouseDown={true}>
      {({ ref: tooltipRef, ...tooltipProps }) => {
        const mergedRefs = mergeRefs(ref, tooltipRef);

        return onClick ? (
          <button
            {...tooltipProps}
            className={className}
            draggable={isDraggable}
            onClick={onClick}
            onDragEnd={onDragEnd}
            onDragStart={onDragStart}
            onKeyDown={onKeyDown || submitOnKeyDown}
            ref={mergedRefs as RefObject<HTMLButtonElement>}
            tabIndex={tabIndex}
            aria-label={tooltip}
            data-color={label.color}
            data-testid={LabelsPopoverTestIds.CardLabel}
          >
            {label.name}
            {children}
          </button>
        ) : (
          <div
            {...tooltipProps}
            className={className}
            onKeyDown={onKeyDown}
            ref={mergedRefs as RefObject<HTMLDivElement>}
            role={onKeyDown && 'menuitem'}
            tabIndex={tabIndex}
            aria-label={tooltip}
            data-color={label.color}
            data-testid={LabelsPopoverTestIds.CardLabel}
          >
            {label.name}
            {children}
          </div>
        );
      }}
    </Tooltip>
  );
};
